import { useState, useEffect, useMemo } from "react";
import API from "../../../../api";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const normalizeIp = (ip) => {
  if (Array.isArray(ip)) return typeof ip[0] === "string" ? ip[0].trim() : "";
  if (typeof ip === "string") return ip.trim();
  return "";
};

const stripPort = (s) =>
  typeof s === "string" && s.includes(":") ? s.split(":")[0].trim() : s;

const getIpKey = (ip) => stripPort(normalizeIp(ip));

const isPrivateIp = (ip) => {
  const value = getIpKey(ip);
  if (!value) return false;
  return (
    value.startsWith("10.") ||
    value.startsWith("192.168.") ||
    /^172\.(1[6-9]|2\d|3[0-1])\./.test(value)
  );
};

export { getIpKey, isPrivateIp };

export const useHighlightsData = () => {
  const [checkStart, setCheckStart] = useState(false);
  const [criticalIpInternal, setCriticalIpInternal] = useState([]);
  const [criticalIpExternal, setCriticalIpExternal] = useState([]);
  const [cveIpInternal, setCveIpInternal] = useState([]);
  const [cveIpExternal, setCveIpExternal] = useState([]);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [cardsSummary, setCardsSummary] = useState({ critical: 0, high: 0, criticalAgent: 0, highAgent: 0 });
  const [mapCountryData, setMapCountryData] = useState([]);
  const [externalIps, setExternalIps] = useState([]);
  //const [intelInternalHosts, setIntelInternalHosts] = useState([]);
  const [dnsCountData, setDnsCountData] = useState({ byHour: [], byRrtype: [], byRrname: [] });
  const [dnsCountLoading, setDnsCountLoading] = useState(false);
  const [anomaliesTop, setAnomaliesTop] = useState([]);
  const [anomaliesRare, setAnomaliesRare] = useState([]);
  const [tiresiaLogs, setTiresiaLogs] = useState([]);
  const [tiresiaLogsLoading, setTiresiaLogsLoading] = useState(false);
  const [activeSection, setActiveSection] = useState(0);
  const [systemData, setSystemData] = useState([]);
  const [countByType, setCountByType] = useState({});

  useEffect(() => {
    //API.getProducts({}).then((res) => setProducts(res.data));
  }, []);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setEventsLoading(true);
        const [internalRes, externalRes] = await Promise.all([
          API.getHighlights({ checkInternal: true }),
          API.getHighlights({ checkInternal: false }),
        ]);
        setEvents((internalRes?.data || []).concat(externalRes?.data || []));
      } catch {
        setEvents([]);
      } finally {
        setEventsLoading(false);
      }
    };
    fetchEvents();
  }, []);

  useEffect(() => {
    API.getLicense().catch(() => {});
  }, []);

  useEffect(() => {
    API.getHighlightsIp()
      .then((res) => {
        const data = res?.data || {};
        setCriticalIpInternal(Array.isArray(data.criticalInternal) ? data.criticalInternal : []);
        setCveIpInternal(Array.isArray(data.cveInternal) ? data.cveInternal : []);
        setCriticalIpExternal(Array.isArray(data.criticalExternal) ? data.criticalExternal : []);
        setCveIpExternal(Array.isArray(data.cveExternal) ? data.cveExternal : []);
        setCheckStart(true);
      })
      .catch(() => {
        setCriticalIpInternal([]);
        setCveIpInternal([]);
        setCriticalIpExternal([]);
        setCveIpExternal([]);
        setCheckStart(true);
      });
  }, []);

  const [cardsSummaryLoading, setCardsSummaryLoading] = useState(true);

  useEffect(() => {
    API.getAnomaliesCountByLecs().catch(() => {});

    API.getHighlights({ type: "cards" })
      .then((res) => setCardsSummary(res.data))
      .catch(() => setCardsSummary({ critical: 0, high: 0, criticalAgent: 0, highAgent: 0 }))
      .finally(() => setCardsSummaryLoading(false));
  }, [criticalIpInternal, criticalIpExternal, cveIpInternal, cveIpExternal]);

  useEffect(() => {    
    const fetch = async () => {
      const lecsMap = (await API.getMap()).data;
      //const agentMap = (await API.getIocDetection()).data;

      const map = (Array.isArray(lecsMap) ? lecsMap : [])
        //.concat(Array.isArray(agentMap) ? agentMap : [])
        .map((item) => ({ countryCode: item?._id?.countryCode || "", count: item?.countIP ?? item?.count ?? 0 }))
        .filter((d) => d.countryCode)
        .sort((a, b) => b.count - a.count);

      setMapCountryData(map);
    }

    fetch();
  }, []);

  useEffect(() => {
    if (activeSection !== 1) return;

    const tz = sessionStorage.getItem("timezone") || "Etc/GMT";
    const now = dayjs().tz(tz);
    setDnsCountLoading(true);

    API.getDnsCounts({ dateFrom: now.subtract(24, "hour").toISOString(), dateTo: now.toISOString() })
      .then((res) => {
        const d = res?.data;
        setDnsCountData({
          byHour: Array.isArray(d?.byHour) ? d.byHour : [],
          byRrtype: Array.isArray(d?.byRrtype) ? d.byRrtype : [],
          byRrname: Array.isArray(d?.byRrname) ? d.byRrname : [],
        });
      })
      .catch(() => setDnsCountData({ byHour: [], byRrtype: [], byRrname: [] }))
      .finally(() => setDnsCountLoading(false));
  }, [activeSection]);

  useEffect(() => {
    API.getTiresiaAnomalies({ hours: 24, mode: "top" })
      .then((res) => setAnomaliesTop(res?.data?.data || []))
      .catch(() => setAnomaliesTop([]));
  }, []);

  useEffect(() => {
    API.getTiresiaAnomalies({ hours: 24, mode: "rare" })
      .then((res) => setAnomaliesRare((res?.data?.data || []).slice(0, 10)))
      .catch(() => setAnomaliesRare([]));
  }, []);

  useEffect(() => {
    let alive = true;
    setTiresiaLogsLoading(true);

    API.getTiresiaLogsList({ limit: 100 })
      .then((res) => { if (alive) setTiresiaLogs(Array.isArray(res?.data) ? res.data : []); })
      .catch(() => { if (alive) setTiresiaLogs([]); })
      .finally(() => { if (alive) setTiresiaLogsLoading(false); });

    return () => { alive = false; };
  }, []);

  // ── Computed values ─────────────────────────────────────────────────────────

  const severitySummary = useMemo(() => {
    const internalHosts = new Set();
    const externalHosts = new Set();
    events.forEach((e) => {
      const srcIp = getIpKey(e?.ipSource);
      const dstIp = getIpKey(e?.ipDestination);
      if (srcIp) (isPrivateIp(srcIp) ? internalHosts : externalHosts).add(srcIp);
      if (dstIp) (isPrivateIp(dstIp) ? internalHosts : externalHosts).add(dstIp);
    });
    return { internalHosts: internalHosts.size, externalHosts: externalHosts.size };
  }, [events]);

  const topInternalHosts = useMemo(() => {
    const counts = new Map();
    events.forEach((e) => {
      [e?.ipSource, e?.ipDestination].forEach((rawIp) => {
        const ip = getIpKey(rawIp);
        if (!ip || !isPrivateIp(ip)) return;
        const c = typeof e?.count === "number" ? e.count : 1;
        counts.set(ip, (counts.get(ip) || 0) + c);
      });
    });
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]).slice(0, 7).map(([ip, value]) => ({ ip, value }));
  }, [events]);

  const topExternalHosts = useMemo(() => {
    const counts = new Map();
    events.forEach((e) => {
      [e?.ipSource, e?.ipDestination].forEach((rawIp) => {
        const ip = getIpKey(rawIp);
        if (!ip || isPrivateIp(ip)) return;
        const c = typeof e?.count === "number" ? e.count : 1;
        counts.set(ip, (counts.get(ip) || 0) + c);
      });
    });
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]).slice(0, 7).map(([ip, value]) => ({ ip, value }));
  }, [events]);

  const sampleCriticalOrHighDetails = useMemo(() => {
    return events
      .filter((e) => e?.tipo === 0 || e?.tipo === 1)
      .sort((a, b) => (b?.count || 0) - (a?.count || 0))
      .slice(0, 2)
      .map((e) => ({
        dettaglio: e?.dettaglio || "Security event",
        ipSource: getIpKey(e?.ipSource) || "—",
        ipDestination: getIpKey(e?.ipDestination) || "—",
        tipo: e?.tipo === 1 ? "critical" : "high",
      }));
  }, [events]);

  const internalPriority = useMemo(() => {
    return Array.from(new Set([...(criticalIpInternal || []), ...(cveIpInternal || [])].map(getIpKey))).filter(Boolean);
  }, [criticalIpInternal, cveIpInternal]);

  const externalPriority = useMemo(() => {
    return Array.from(new Set([...(criticalIpExternal || []), ...(cveIpExternal || [])].map(getIpKey))).filter(Boolean);
  }, [criticalIpExternal, cveIpExternal]);

  const topInt = useMemo(() => {
    return topInternalHosts.slice(0, 4).map((r) => `${r.ip} (${r.value.toLocaleString()} events)`).join(", ");
  }, [topInternalHosts]);

  const topExt = useMemo(() => {
    return topExternalHosts.slice(0, 4).map((r) => `${r.ip} (${r.value.toLocaleString()} events)`).join(", ");
  }, [topExternalHosts]);

  const topAnomaliesBehaviors = useMemo(() => {
    const list = (anomaliesTop || [])
      .map((e) => {
        const s = `${e?.ipA ?? ""} → ${e?.ipB ?? ""}`;
        return { label: s.length > 35 ? `${s.slice(0, 32)}...` : s, value: Number(e?.count ?? 0) };
      })
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
    return { labels: list.map((x) => x.label), values: list.map((x) => x.value) };
  }, [anomaliesTop]);

  const topAnomaliesLogsRarest = useMemo(() => {
    const list = (anomaliesRare || [])
      .map((e) => {
        const s = `${e?.ipA ?? ""} → ${e?.ipB ?? ""}`;
        return { label: s.length > 35 ? `${s.slice(0, 32)}...` : s, value: Number(e?.count ?? 0) };
      })
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
    return { labels: list.map((x) => x.label), values: list.map((x) => x.value) };
  }, [anomaliesRare]);

  const fmtPriorityList = (arr) => {
    const maxList = 8;
    return arr.length <= maxList
      ? arr.join(", ")
      : `${arr.slice(0, maxList).join(", ")} and ${arr.length - maxList} more`;
  };

  useEffect(() => {
    API.getSystemData({}).then((res) => {
      const data = res?.data ?? [];
      setSystemData(data);
      setCountByType(data.reduce((acc, item) => {
        const type = item.eventType;
        if (type) acc[type] = (acc[type] || 0) + 1;
        return acc;
      }, {}));
    }).catch(() => {
      setSystemData([]);
      setCountByType({});
    });
  }, []);

  useEffect(() => {
    API.getExternalIps().then((res) => {
      setExternalIps(res.data);
    }).catch(() => {
      setExternalIps([]);
    })
  }, [])

  return {
    // state
    //products,
    checkStart,
    events,
    eventsLoading,
    cardsSummaryLoading,
    cardsSummary,
    mapCountryData,
    //intelExternalHosts,
    //intelInternalHosts,
    externalIps,
    dnsCountData,
    dnsCountLoading,
    anomaliesTop,
    anomaliesRare,
    tiresiaLogs,
    tiresiaLogsLoading,
    activeSection,
    setActiveSection,
    // computed
    severitySummary,
    sampleCriticalOrHighDetails,
    internalPriority,
    externalPriority,
    topInt,
    topExt,
    topAnomaliesBehaviors,
    topAnomaliesLogsRarest,
    fmtPriorityList,
    systemData,
    countByType,
  };
};
